import { keychain } from './keychain'
import { stopWS } from './ws'
import { useAppStore } from '../store/app'
import { logoutDevice } from './client'
import { clearChatDrafts } from '../screens/chat/drafts'

/// Full sign-out. Tells the server to revoke this device, closes the
/// WebSocket, wipes every keychain key the app wrote (both device-local
/// and iCloud-synced), and resets the store to unregistered. The next
/// render flips `isRegistered → false` so RootView swaps back to
/// RegistrationScreen.
///
/// Takes no arguments. It used to accept a pendingInviteToken to carry an
/// invite across the keychain wipe — a logout function needing to preserve
/// an invitation was the clearest sign the bearer model didn't fit.
/// Invitations now live server-side keyed on email, so signing back in as
/// the invited address surfaces them again on its own.
export async function logout(): Promise<void> {
  // Best-effort server revoke first — if the network is offline, fall
  // through to local wipe so the user can still leave the app. The
  // server will eventually GC the row via the silent-device sweeper.
  try {
    await logoutDevice()
  } catch (err) {
    console.warn('logout: server revoke failed (continuing)', err)
  }
  stopWS()
  try {
    await keychain.wipeAll()
  } catch (err) {
    console.warn('logout: keychain wipe failed', err)
  }
  resetStoreToUnregistered()
}

/// Remote-revocation sign-out. Same local teardown as logout(), but skips
/// the /logout server call (our credentials are already dead — the server
/// revoked them) and records why so RegistrationScreen can explain it
/// instead of silently dropping the user on the login screen. Called when
/// probeEnrollment() confirms the device is no longer enrolled — the fix
/// for "unenrolled user stuck in a limbo 'disconnected' app". Idempotent:
/// no-ops once we're already unregistered.
export async function signOutRemotely(): Promise<void> {
  if (!useAppStore.getState().isRegistered) return
  stopWS()
  try {
    await keychain.wipeAll()
  } catch (err) {
    console.warn('signOutRemotely: keychain wipe failed', err)
  }
  resetStoreToUnregistered()
  // Set AFTER the reset — clearRegistration() (inside resetStoreToUnregistered)
  // clears sessionEndedReason, so record it last so the notice survives.
  useAppStore.getState().setSessionEndedReason('revoked')
}

function resetStoreToUnregistered(): void {
  const s = useAppStore.getState()
  s.clearRegistration()
  // Clear transient state too — any cached agent / rule / transcript
  // data belonged to the logged-out account. Don't call
  // replaceOrganizations([]) here: that reducer also flips
  // hasLoadedOrganizations back to true, which causes the very next
  // registration on the same app instance to land in OrphanedScreen
  // (the orphan check trips on hasLoaded + empty). clearRegistration
  // above already wiped availableOrganizations + hasLoadedOrganizations
  // correctly; leave it alone.
  // Drops every household's buckets, not just the selected one — the
  // whole cache belonged to the logged-out account.
  s.clearHouseholdState()
  s.replaceMyInvites([])
  s.clearTranscript()
  while (useAppStore.getState().sheets.length > 0) s.popSheet()
  while (useAppStore.getState().navStack.length > 0) s.popNav()
  // Unsent chat drafts belonged to the logged-out account too.
  clearChatDrafts()
}
